import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { GlobalOutlined } from '@ant-design/icons';
import { Dropdown, Space } from 'antd';
import i18next, { t } from 'i18next';
import { validate as uuidValidate } from 'uuid';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import QuickTripLogo from '../../assets/imgs/quick_trip_logo.svg?react';
import { getSelectableLanguages } from '../../assets/languages/configs';
import CustomAntButton from '../../components/CustomAntButton/CustomAntButton';
import CenterLoading from '../../components/CenterLoading/CenterLoading';
import TextBreaks from '../../components/TextBreaks/TextBreaks';

import useQrCode from '../../hooks/useDisplayQrCode';
import useLocalizedTime from '../../hooks/useLocalizedTime';

import { MultilingualType } from 'types';

import { applyFontFromLanguage, getValueByLanguage, showNotificationDialog } from '../../utils';

import styles from './styles.module.css';

const WebQrDisplayScreen = () => {
    const [language, setLanguage] = useState(i18next.language);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { distributionQrCode, fetchDistributionQrCode, isLoading, receiveDistributionTicket } = useQrCode();
    const { formatTime } = useLocalizedTime();
    const location = useLocation();

    const receiveId = searchParams.get('receiveId');

    const languages = useMemo(() => {
        const selectableLanguages = getSelectableLanguages();
        const languages = selectableLanguages.flatMap((item, index) => {
            if (selectableLanguages.length - 1 <= index) {
                return {
                    label: <div className={styles.language}>{item.title}</div>,
                    key: item.key,
                };
            } else {
                return [
                    {
                        label: <div className={styles.language}>{item.title}</div>,
                        key: item.key,
                    },
                    {
                        type: 'divider' as const,
                    },
                ];
            }
        });
        return languages;
    }, []);

    const l = useCallback(
        (text?: MultilingualType | null) => {
            return getValueByLanguage(text, language);
        },
        [language],
    );

    useLayoutEffect(() => {
        if (receiveId && uuidValidate(receiveId)) {
            void fetchDistributionQrCode({
                distributionCode: receiveId,
            });
        } else {
            navigate('/buy');
        }
    }, [receiveId]);

    /**
     * 言語変更の処理
     */
    const handleLangueChange = ({ key }: { key: string }) => {
        try {
            // 言語を切り替え
            setLanguage(key);
            void i18next.changeLanguage(key);
            applyFontFromLanguage(key);

            // ローカルストレージに言語値保存
            localStorage.setItem('lang', key);

            // 言語変更に完了のメッセージを表示
            showNotificationDialog(
                'success',
                'language-change',
                t('notification.title'),
                t('notification.language-change'),
            );
        } catch (error) {
            console.log(error);
        }
    };

    const handleDisplayQrCode = async () => {
        const message = await receiveDistributionTicket({
            orderDistributionId: distributionQrCode?.orderDistributionId,
            distributionCode: distributionQrCode?.distributionCode,
        });
        if (message === 'succeeded') {
            navigate('/bought/ticket', {
                state: {
                    distributionQrCode: {
                        distributionCode: distributionQrCode?.distributionCode,
                        redirectUrl: location.pathname + location.search,
                    },
                },
            });
        }
    };

    return (
        <div className={styles.wrapper}>
            {isLoading && <CenterLoading overFooter />}
            <div className={styles.header}>
                <QuickTripLogo className={styles.quickTripLogo} />
            </div>
            {distributionQrCode ? (
                <div className={`content-container scrollable ${styles.container}`}>
                    <div className={styles.languages}>
                        <Dropdown
                            className={styles.dropdown}
                            menu={{
                                items: languages,
                                onClick: handleLangueChange,
                            }}
                            trigger={['click']}
                        >
                            <Space>
                                <GlobalOutlined />
                                {languages.find(({ key }) => key === language)?.label}
                            </Space>
                        </Dropdown>
                    </div>
                    <div className={styles.infoGroup}>
                        <div className={styles.ticketName}>
                            <TextBreaks text={l(distributionQrCode.ticketName)} />
                        </div>
                        <div className={styles.descriptions}>
                            <TextBreaks text={l(distributionQrCode.subtitle)} />
                        </div>
                    </div>
                    <div className={styles.infoGroup}>
                        <div className={styles.descriptions}>
                            <TextBreaks text={l(distributionQrCode?.stockPartitionName)} />
                        </div>
                        {distributionQrCode?.reserveOn != null && (
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('reservation.usage-time')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>{formatTime(distributionQrCode?.reserveOn, 'lll')}</div>
                            </div>
                        )}
                        {distributionQrCode?.representativeName != null && (
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('reservation.user-name')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>{distributionQrCode.representativeName}</div>
                            </div>
                        )}
                        <div className={styles.textContent}>
                            <span className={styles.label}>{t('ticket-detail.number-label')}</span>
                            <span className={styles.divider}>:</span>
                            <div className={styles.value}>
                                {(distributionQrCode.numberAdults ?? 0) > 0 && (
                                    <span className={styles.numberContext}>
                                        {t('ticket-detail.number', {
                                            label: t('ticket-detail.adult-label'),
                                            number: distributionQrCode.numberAdults,
                                        })}
                                    </span>
                                )}
                                {(distributionQrCode.numberCustom1 ?? 0) > 0 && (
                                    <span className={styles.numberContext}>
                                        {t('ticket-detail.number', {
                                            label: getValueByLanguage(distributionQrCode.custom1Name, language),
                                            number: distributionQrCode.numberCustom1,
                                        })}
                                    </span>
                                )}
                                {(distributionQrCode.numberCustom2 ?? 0) > 0 && (
                                    <span className={styles.numberContext}>
                                        {t('ticket-detail.number', {
                                            label: getValueByLanguage(distributionQrCode.custom2Name, language),
                                            number: distributionQrCode.numberCustom2,
                                        })}
                                    </span>
                                )}
                                {(distributionQrCode.numberCustom3 ?? 0) > 0 && (
                                    <span className={styles.numberContext}>
                                        {t('ticket-detail.number', {
                                            label: getValueByLanguage(distributionQrCode.custom3Name, language),
                                            number: distributionQrCode.numberCustom3,
                                        })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles.actionsBtn}>
                        <CustomAntButton className={styles.button} type="outlined" block onClick={handleDisplayQrCode}>
                            {t('button.view-qr-code')}
                        </CustomAntButton>
                    </div>
                </div>
            ) : (
                <div className={styles.noData}>{t('loading.small-bought-ticket')}</div>
            )}
        </div>
    );
};

export default WebQrDisplayScreen;
